import { createBusinessClassificationApi, updateBusinessClassificationApi } from "@/api/services/global/master";
import { TMasterBusinessClassification } from "@/api/entities/master-business-classification.entity";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataBusinessClassificationSchema } from "@/features/master-data/schema/masterDataBusinessClassificationSchema";
import { TMasterBusinessClassificationForm } from "@/features/master-data/types/masterDataBusinessClassificationForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TBusinessClassificationFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedBusinessClassification?: TMasterBusinessClassification;
};

export const BusinessClassificationFormModal: FC<TBusinessClassificationFormModalProps> = ({ onClose, isOpen, selectedBusinessClassification }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      businessClassificationNumber: selectedBusinessClassification ? selectedBusinessClassification.businessClassificationNumber : undefined,
      businessCategory: selectedBusinessClassification ? selectedBusinessClassification.businessCategory : undefined,
      informationDisplay: selectedBusinessClassification ? selectedBusinessClassification.informationDisplay : undefined,
    }),
    [selectedBusinessClassification],
  );

  const methods = useForm<TMasterBusinessClassificationForm>({
    resolver: yupResolver(masterDataBusinessClassificationSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control } = methods;

  const onSubmit = async (data: TMasterBusinessClassificationForm) => {
    try {
      if (selectedBusinessClassification) {
        const rs = await updateBusinessClassificationApi({
          businessClassifications: [
            {
              id: toNumber(selectedBusinessClassification.id),
              businessClassificationNumber: data.businessClassificationNumber,
              businessCategory: data.businessCategory,
              informationDisplay: data.informationDisplay,
            },
          ],
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createBusinessClassificationApi({
          businessClassificationNumber: data.businessClassificationNumber,
          businessCategory: data.businessCategory,
          informationDisplay: data.informationDisplay,
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_BUSINESS_CLASSIFICATION] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal title={selectedBusinessClassification ? "編集" : "追加"} isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataBusinessClassificationSchema}>
        <Stack gap={3} my={3}>
          <Grid item xs={6}>
            <FormField control={control} name="businessClassificationNumber">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="businessCategory">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="informationDisplay">
              <Input />
            </FormField>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
